import React from "react";
import {
  FileText,
  Users,
  List,
  CheckCircle,
  AlertCircle,
  Info,
  ArrowRight,
  Settings,
  TestTube,
  Server,
  User,
} from "lucide-react";

interface WF_FMS_Finance_Viewer_Use_CaseProps {}

const WF_FMS_Finance_Viewer_Use_Case: React.FC<WF_FMS_Finance_Viewer_Use_CaseProps> = () => {
  return (
    <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 bg-white font-sans">
      {/* Heading */}
      <h1 className="text-2xl font-bold text-gray-900 mb-6 border-b border-gray-300 pb-2">
        WF_ FMS Finance Viewer
      </h1>
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8">
        {/* Description */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <Info size={18} className="mr-2 text-blue-600" />
            Description
          </h2>
          <p className="text-gray-700 leading-relaxed">
            The Finance Viewer module in the Financial Management System (FMS)
            allows bank officers to search and view detailed finance information
            for customer accounts after disbursal. The Finance Query screen
            provides flexible search options using various identifiers like
            Finance ID, Customer Name, etc. Once identified, the system displays
            comprehensive finance details retrieved from the Loan Origination
            System (LOS).
          </p>
        </section>

        {/* Actors */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <Users size={18} className="mr-2 text-blue-600" />
            Actors
          </h2>
          <div className="text-gray-700">
            <h3 className="font-medium text-gray-800 mb-2">Business User</h3>
            <ul className="list-disc pl-5 mb-4">
              <li>Bank Officer</li>
            </ul>
            <h3 className="font-medium text-gray-800 mb-2">System Roles</h3>
            <ul className="list-disc pl-5 mb-4">
              <li>Financial Management System (FMS)</li>
            </ul>
            <h3 className="font-medium text-gray-800 mb-2">Stakeholders</h3>
            <ul className="list-disc pl-5">
              <li>Loan Operations</li>
              <li>Credit</li>
              <li>Customer Service</li>
            </ul>
          </div>
        </section>

        {/* User Actions & System Responses */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <List size={18} className="mr-2 text-blue-600" />
            User Actions & System Responses
          </h2>
          <ol className="list-decimal pl-5 text-gray-700">
            <li>Bank Officer logs into the FMS application.</li>
            <li>Navigates to the Finance Query screen.</li>
            <li>
              Searches for a finance deal using parameters such as Finance ID,
              Customer Name, or Branch.
            </li>
            <li>
              System retrieves matching finance record(s) and displays detailed
              view-only information including:
              <ul className="list-disc pl-5 mt-2">
                <li>
                  Application Info:
                  <ul className="list-circle pl-5 mt-1">
                    <li>Finance ID</li>
                    <li>Date</li>
                    <li>Branch Name</li>
                    <li>Customer Name</li>
                    <li>Co-applicant</li>
                    <li>Guarantor</li>
                  </ul>
                </li>
                <li>
                  Financial Info:
                  <ul className="list-circle pl-5 mt-1">
                    <li>Requested & Sanctioned Amounts</li>
                    <li>Disbursal Type</li>
                    <li>Disbursal Date</li>
                    <li>Tenure</li>
                  </ul>
                </li>
                <li>
                  Employment Info:
                  <ul className="list-circle pl-5 mt-1">
                    <li>Employer Name</li>
                    <li>Employee Code</li>
                  </ul>
                </li>
                <li>
                  Installments:
                  <ul className="list-circle pl-5 mt-1">
                    <li>Installment Plan</li>
                    <li>Frequency</li>
                    <li>Mode</li>
                    <li>No. of Installments</li>
                    <li>Advance Installment</li>
                    <li>Due Date</li>
                    <li>First Installment Date</li>
                  </ul>
                </li>
              </ul>
            </li>
            <li>
              Bank Officer views the details; system restricts any modification.
            </li>
          </ol>
        </section>

        {/* Precondition */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <CheckCircle size={18} className="mr-2 text-green-600" />
            Precondition
          </h2>
          <ul className="list-disc pl-5 text-gray-700">
            <li>Finance must be disbursed and recorded in FMS.</li>
          </ul>
        </section>

        {/* Post Condition */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <CheckCircle size={18} className="mr-2 text-green-600" />
            Post Condition
          </h2>
          <ul className="list-disc pl-5 text-gray-700">
            <li>Finance details are displayed in read-only mode.</li>
          </ul>
        </section>

        {/* Straight Through Process (STP) */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <ArrowRight size={18} className="mr-2 text-blue-600" />
            Straight Through Process (STP)
          </h2>
          <p className="text-gray-700 leading-relaxed">
            Login → Navigate to Finance Query Screen → Enter Search Criteria →
            View Finance Details → Logout
          </p>
        </section>

        {/* Alternative Flows */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <ArrowRight size={18} className="mr-2 text-blue-600" />
            Alternative Flows
          </h2>
          <ul className="list-disc pl-5 text-gray-700">
            <li>Search by Co-applicant or Guarantor Name.</li>
            <li>Search using Branch and Disbursal Date filters.</li>
          </ul>
        </section>

        {/* Exception Flows */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <AlertCircle size={18} className="mr-2 text-red-600" />
            Exception Flows
          </h2>
          <ul className="list-disc pl-5 text-gray-700">
            <li>Invalid Finance ID entered.</li>
            <li>No matching records found.</li>
          </ul>
        </section>

        {/* User Activity Diagram (Flowchart) */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <List size={18} className="mr-2 text-blue-600" />
            User Activity Diagram (Flowchart)
          </h2>
          <p className="text-gray-700 leading-relaxed">
            Start → Login → Enter Query → System Fetches Data → Display Details
            → End
          </p>
        </section>

        {/* Parking Lot */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <Info size={18} className="mr-2 text-blue-600" />
            Parking Lot
          </h2>
          <ul className="list-disc pl-5 text-gray-700">
            <li>Enable export of finance details to PDF or Excel.</li>
            <li>Add bookmark or save search functionality.</li>
          </ul>
        </section>

        {/* System Components Involved */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <Settings size={18} className="mr-2 text-blue-600" />
            System Components Involved
          </h2>
          <div className="text-gray-700">
            <h3 className="font-medium text-gray-800 mb-2">UI</h3>
            <ul className="list-disc pl-5 mb-4">
              <li>Finance Viewer Screen in FMS</li>
            </ul>
            <h3 className="font-medium text-gray-800 mb-2">APIs</h3>
            <ul className="list-disc pl-5 mb-4">
              <li>Data Fetch from LOS</li>
            </ul>
            <h3 className="font-medium text-gray-800 mb-2">DB Tables</h3>
            <ul className="list-disc pl-5 mb-4">
              <li>Finance Master</li>
              <li>Customer Details</li>
              <li>Installment Schedules</li>
            </ul>
            <h3 className="font-medium text-gray-800 mb-2">Services</h3>
            <ul className="list-disc pl-5">
              <li>Query Engine</li>
              <li>Read-Only Data Display Handler</li>
            </ul>
          </div>
        </section>

        {/* Test Scenarios */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <TestTube size={18} className="mr-2 text-blue-600" />
            Test Scenarios
          </h2>
          <ul className="list-disc pl-5 text-gray-700">
            <li>Valid search using Finance ID.</li>
            <li>Search by Customer Name or Branch.</li>
            <li>Attempt to edit read-only data (negative test).</li>
            <li>Search with no results.</li>
          </ul>
        </section>

        {/* Infra & Deployment Notes */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <Server size={18} className="mr-2 text-blue-600" />
            Infra & Deployment Notes
          </h2>
          <ul className="list-disc pl-5 text-gray-700">
            <li>High system availability needed for frequent access.</li>
            <li>Logging enabled for audit of search/view activities.</li>
          </ul>
        </section>

        {/* Dev Team Ownership */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <User size={18} className="mr-2 text-blue-600" />
            Dev Team Ownership
          </h2>
          <p className="text-gray-700 leading-relaxed">
            Squad: Finance Information Services Team
            <br />
            Contact: Lead Dev - finance_viewer@bankdomain.com
            <br />
            JIRA: WF-FINANCE-VIEW-01
            <br />
            Git Repo: /fms/finance-viewer
          </p>
        </section>
      </div>
    </main>
  );
};

export default WF_FMS_Finance_Viewer_Use_Case;